"use client";

import React, { useState } from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
import { Mountain, Waves, Trees, ThermometerSun, Map, ArrowUpRight, Ruler, LoaderCircle, Compass } from "lucide-react";
const formatStat = (val: any, suffix: string) => {
  if (!val) return "N/A";
  return `${val}${suffix}`;
};
function ArchiveConsumer() {
  const {
    posts,
    loading,
    error,
    setQuery,
    isRefetching
  } = usePosts();
  const [activeFilter, setActiveFilter] = useState("all");
  const handleFilterChange = (value: string) => {
    if (!value) return;
    setActiveFilter(value);
    const categoryMap: Record<string, number[]> = {
      all: [],
      desert: [10],
      alpine: [11],
      coastal: [12],
      forest: [13]
    };
    const categories = categoryMap[value] || [];
    setQuery(prev => ({
      ...prev,
      categories: categories.length > 0 ? categories : undefined,
      offset: 0
    }));
  };
  return <div className="w-full space-y-10">
      {}
      <div className="flex flex-col items-center justify-center space-y-4">
        <h3 className="text-sm font-mono uppercase tracking-widest text-muted-foreground">
          Select Terrain
        </h3>
        <ToggleGroup type="single" value={activeFilter} onValueChange={handleFilterChange} className="bg-secondary/30 p-1 rounded-lg border border-border/50">
          <ToggleGroupItem value="all" aria-label="All Terrains" className="gap-2 px-4 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
            <Compass className="h-4 w-4" />
            <span className="hidden sm:inline">All</span>
          </ToggleGroupItem>
          <ToggleGroupItem value="desert" aria-label="Desert" className="gap-2 px-4 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
            <ThermometerSun className="h-4 w-4" />
            <span className="hidden sm:inline">Desert</span>
          </ToggleGroupItem>
          <ToggleGroupItem value="alpine" aria-label="Alpine" className="gap-2 px-4 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
            <Mountain className="h-4 w-4" />
            <span className="hidden sm:inline">Alpine</span>
          </ToggleGroupItem>
          <ToggleGroupItem value="coastal" aria-label="Coastal" className="gap-2 px-4 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
            <Waves className="h-4 w-4" />
            <span className="hidden sm:inline">Coastal</span>
          </ToggleGroupItem>
          <ToggleGroupItem value="forest" aria-label="Forest" className="gap-2 px-4 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
            <Trees className="h-4 w-4" />
            <span className="hidden sm:inline">Forest</span>
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {}
      <div className="min-h-[400px]">
        {loading && !isRefetching && posts.length === 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-[500px] rounded-xl bg-muted/20 animate-pulse border border-border/40" />)}
          </div> : error ? <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
            <p className="text-destructive">Unable to load expedition data.</p>
            <Button variant="outline" onClick={() => window.location.reload()}>Retry Connection</Button>
          </div> : posts.length === 0 ? <div className="flex flex-col items-center justify-center py-20 text-center space-y-4 border border-dashed border-border rounded-xl bg-muted/5">
            <Map className="h-12 w-12 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No expeditions found for this terrain.</p>
            <Button variant="link" onClick={() => handleFilterChange("all")}>Clear Filters</Button>
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, i) => <Card key={post.id} data-index={i} className="group flex flex-col overflow-hidden border-border/60 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-colors duration-300 h-full">
                {}
                <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-secondary/50">
                      <Map className="h-12 w-12 text-muted-foreground/50" />
                    </div>}
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent opacity-60" />
                  
                  {}
                  {post.categories && post.categories.length > 0 && <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center rounded-sm bg-background/80 px-2.5 py-0.5 text-xs font-medium text-foreground backdrop-blur-md border border-border/50 shadow-sm">
                        {post.categories[0].name}
                      </span>
                    </div>}
                </div>

                {}
                <CardHeader className="space-y-2 pb-4">
                  <CardTitle className="text-xl font-bold leading-tight tracking-tight">
                    <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                      {post.title}
                    </Link>
                  </CardTitle>
                  <CardDescription className="line-clamp-2 text-muted-foreground/80">
                    {post.excerpt ? <span dangerouslySetInnerHTML={{
                __html: post.excerpt
              }} /> : "No mission brief available for this expedition."}
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-grow" />

                {}
                <CardFooter className="p-0 border-t border-border/40 bg-secondary/10">
                  <div className="grid grid-cols-2 w-full divide-x divide-border/40">
                    <div className="p-4 flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-xs font-mono uppercase text-muted-foreground tracking-wider">
                        <Ruler className="h-3 w-3" />
                        <span>Distance</span>
                      </div>
                      <span className="font-mono font-semibold text-foreground">
                        {formatStat(post.customFields?.distance_traveled, " mi")}
                      </span>
                    </div>
                    <div className="p-4 flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-xs font-mono uppercase text-muted-foreground tracking-wider">
                        <ArrowUpRight className="h-3 w-3" />
                        <span>Elevation</span>
                      </div>
                      <span className="font-mono font-semibold text-foreground">
                        {formatStat(post.customFields?.elevation_gain, " ft")}
                      </span>
                    </div>
                  </div>
                </CardFooter>
              </Card>)}
          </div>}
        
        {isRefetching && posts.length > 0 && <div className="flex justify-center py-8">
            <LoaderCircle className="h-6 w-6 animate-spin text-primary" />
          </div>}
      </div>
    </div>;
}
export default function TheAdventureArchives() {
  const wp_query_params = {
    post_type: "post",
    per_page: 9,
    orderby: "date",
    order: "desc",
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="the-adventure-archives" className="dark bg-background text-foreground py-20 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562326/img/topo_pattern_dark.png)] bg-repeat bg-[length:400px_400px]" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col items-center text-center mb-12 space-y-4">
          <div className="inline-flex items-center justify-center p-2 bg-secondary/30 rounded-full mb-2 border border-border/50">
            <Map className="h-5 w-5 text-primary mr-2" />
            <span className="text-xs font-mono uppercase tracking-widest text-primary">Expedition Log</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            The Adventure Archives
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl">
            A comprehensive database of our journeys across the American West. 
            Filter by terrain to explore specific technical builds and route data.
          </p>
        </div>

        <PostsProvider wp_query={wp_query} className="w-full">
          <ArchiveConsumer />
        </PostsProvider>

        <div className="mt-16 flex justify-center">
          <Link to="/TravelLogPage">
            <Button variant="outline" size="lg" className="border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground">
              View Full Logbook
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}